import { ReactNode, useCallback, useEffect, useRef } from "react";
import { captureMessage } from "@sentry/react";

import { useVersionCheckStore } from "~/store/slices/versionCheck.slice";
import { useAnalyticsStore } from "~/store/slices/analytics.slice";
import { postCrossTabSync, subscribeCrossTab, safeLocalStorage } from "~/utils";
import {
  fetchBackendRequiredVersion,
  fetchFrontendBuildVersion,
  getCurrentFrontendBuildVersion
} from "~/services/version-check/api";
import {
  RELOAD_COOLDOWN_MS,
  RELOAD_COOLDOWN_STORAGE_KEY,
  SHOWN_FLAG_STORAGE_PREFIX,
  VERSION_CHECK_INTERVAL_MS
} from "~/services/version-check/constants";

import { UpdateAvailableModal } from "./UpdateAvailableModal";

const isInReloadCooldown = (): boolean => {
  try {
    const raw = sessionStorage.getItem(RELOAD_COOLDOWN_STORAGE_KEY);
    if (!raw) return false;
    const reloadedAt = Number(raw);
    return Number.isFinite(reloadedAt) && Date.now() - reloadedAt < RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const markReloadCooldown = (): void => {
  try {
    sessionStorage.setItem(RELOAD_COOLDOWN_STORAGE_KEY, String(Date.now()));
  } catch {}
};

interface Props {
  children: ReactNode;
}

/**
 * LKP-4006 — обнаружение устаревшей версии SPA/backend.
 *
 * Два независимых сигнала пишут в общий useVersionCheckStore:
 *  A. Сборка SPA: fetch('/index.html', no-store) сравнивается с meta-тегом текущей страницы (этот тик).
 *  B. Backend: GET /api/version, frontendContractVersion сравнивается с backendBaseline, зафиксированным
 *     первым успешным ответом при старте приложения (этот тик).
 *
 * Ни один из путей не вызывает reload() автоматически — единственный вызов перезагрузки
 * во всём модуле находится в handleReload, привязанном к клику пользователя по кнопке
 * в модалке. Поэтому бесконечный цикл перезагрузок структурно невозможен.
 *
 * Поллинг (A и B) не тикает, пока вкладка в фоне — таймер останавливается на visibilitychange
 * и перезапускается с немедленной проверкой при возврате фокуса.
 */
export const VersionCheckContainer = ({ children }: Props) => {
  const outdated = useVersionCheckStore(state => state.outdated);
  const markOutdated = useVersionCheckStore(state => state.markOutdated);

  const feBaselineRef = useRef<string | null>(null);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    feBaselineRef.current = getCurrentFrontendBuildVersion();
  }, []);

  const runCheck = useCallback(async () => {
    if (isCheckingRef.current || useVersionCheckStore.getState().outdated || isInReloadCooldown())
      return;
    isCheckingRef.current = true;

    try {
      const [feVersion, beResult] = await Promise.all([
        fetchFrontendBuildVersion(),
        fetchBackendRequiredVersion()
      ]);

      if (feBaselineRef.current && feVersion && feVersion !== feBaselineRef.current) {
        markOutdated({
          source: "frontend",
          oldVersion: feBaselineRef.current,
          newVersion: feVersion
        });
        return;
      }

      if (beResult !== null) {
        const { backendBaseline, setBackendBaseline } = useVersionCheckStore.getState();

        if (backendBaseline === null) {
          setBackendBaseline(beResult.frontendContractVersion);
        } else if (beResult.frontendContractVersion !== backendBaseline) {
          markOutdated({
            source: "backend",
            oldVersion: backendBaseline,
            newVersion: beResult.frontendContractVersion
          });
        }
      }
    } finally {
      isCheckingRef.current = false;
    }
  }, [markOutdated]);

  useEffect(() => {
    if (!outdated) return;

    const { source, oldVersion, newVersion } = useVersionCheckStore.getState();
    if (source === null || oldVersion === null || newVersion === null) return;

    postCrossTabSync("outdated");

    const shownFlagKey = `${SHOWN_FLAG_STORAGE_PREFIX}${newVersion}`;
    if (safeLocalStorage.getItem(shownFlagKey)) return;
    safeLocalStorage.setItem(shownFlagKey, "1");

    const extra = { oldVersion, newVersion };
    useAnalyticsStore.getState().trackEvent("update_prompt_shown", { source, ...extra });
    captureMessage("stale_spa_detected", {
      level: "info",
      tags: { source },
      extra
    });
  }, [outdated]);

  useEffect(() => {
    let intervalId: number | null = null;

    const startPolling = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(runCheck, VERSION_CHECK_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalId === null) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runCheck();
        startPolling();
      } else {
        stopPolling();
      }
    };

    runCheck();
    if (document.visibilityState === "visible") {
      startPolling();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const unsubscribeCrossTab = subscribeCrossTab(message => {
      if (message === "outdated") {
        markOutdated();
      }
    });

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      unsubscribeCrossTab();
    };
  }, [runCheck, markOutdated]);

  const handleReload = () => {
    markReloadCooldown();
    window.location.reload();
  };

  return (
    <>
      {children}
      <UpdateAvailableModal isShow={outdated} onReload={handleReload} />
    </>
  );
};
